import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DomainSidebar, DomainSidebarHeader, DomainTwoColumnContent, DomainTwoColumnLayout } from "@/components/ui/DomainSidebar";
import { NewTagModal } from "../components/NewTagModal";
import { useCreateTag, useDeleteTag, useTags, useTagsBalance, useUpdateTag, useTagColors, useTagIcons, type TagInput } from "@/hooks/useTags";
import { formatCurrency, formatPercent as formatPercentValue, formatSignedCurrency } from "@/utils/formatters";
import { confirmAction, showAppMessage } from "@/components/ui/confirmDialog";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { TagCard } from "../components/TagCard";
import "../tags.css";
import { useNavigationState } from "@/hooks/useNavigationState";
import { SearchInput } from "@/components/ui/SearchInput";
import { EntityTransactionsModal } from "@/domains/accounts/components/AccountTransactionsModal";
import { SelectInput } from "@/components/ui/primitives";
import { TagDistributionDonut } from "../components/TagDistributionDonut";

type SelectedTag = {
  id: number;
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
};

interface TagRow {
  id: number;
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
  tag_total_expense: number;
  tag_total_deposit: number;
  /** Share of tagged expenses (0–100). Derived from backend field when available, else computed locally. */
  share: number;
}

type TagSort = "expense-desc" | "expense-asc" | "name-asc" | "name-desc";

const formatMoney = (value: number) => formatCurrency(value);
const formatExpenseMoney = (value: number) => formatSignedCurrency(-Math.abs(value));

const formatPercent = (value: number) =>
  formatPercentValue(value, undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const TagsView = () => {
  const { t } = useTranslation(["common", "tags", "accounts", "categories"]);
  const [searchTerm, setSearchTerm] = useNavigationState("tags.searchTerm", "");
  const [sortBy, setSortBy] = useNavigationState<TagSort>("tags.sortBy", "expense-desc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<SelectedTag | null>(null);
  const [detailsTag, setDetailsTag] = useState<TagRow | null>(null);


  const {
    data: tagsBalanceData,
    isLoading: loadingTagsBalance,
    error: tagsBalanceError,
  } = useTagsBalance();
  const { data: tags = [], isLoading: loadingTags, error: tagsError } = useTags();
  const { data: tagColors = [] } = useTagColors();
  const { data: tagIcons = [] } = useTagIcons();
  const colorById = new Map(tagColors.map((color) => [color.id, color]));
  const iconById = new Map(tagIcons.map((icon) => [icon.id, icon]));
  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();

  // Shown as context under the tagged total so the user understands why the number is smaller
  // than their grand total of expenses.
  const untaggedExpense = Number(tagsBalanceData?.untagged_total_expense || 0);
  // Prefer the backend-computed value; fall back to the local derivation so the sidebar never
  // shows $0 when web deploys ahead of an API version that predates tagged_total_expense.
  const taggedExpense =
    tagsBalanceData?.tagged_total_expense != null
      ? Number(tagsBalanceData.tagged_total_expense)
      : Number(tagsBalanceData?.total_expense || 0) - untaggedExpense;

  const enrichedTags = useMemo(() => {
    const tagsBalance = tagsBalanceData?.tags_balance ?? [];
    const tagMap = new Map(tags.map((tag) => [tag.id, tag]));
    const balanceMap = new Map(tagsBalance.map((tag) => [tag.id, tag]));

    const toShare = (pctFromBackend: number | undefined, expenseAmount: number) =>
      pctFromBackend != null
        ? pctFromBackend
        : taggedExpense > 0 ? (expenseAmount / taggedExpense) * 100 : 0;

    const completeTags: TagRow[] = tags.map((tag) => {
      const balance = balanceMap.get(tag.id);
      const tag_total_expense = Number(balance?.tag_total_expense ?? 0);
      return {
        id: tag.id,
        name: tag.name,
        color_id: tag.color_id ?? null,
        icon_id: tag.icon_id ?? null,
        description: tag.description ?? balance?.description,
        tag_total_expense,
        tag_total_deposit: Number(balance?.tag_total_deposit ?? 0),
        share: toShare(balance?.percentage_of_tagged_expense, tag_total_expense),
      };
    });

    const orphanBalanceTags: TagRow[] = tagsBalance
      .filter((balanceTag) => !tagMap.has(balanceTag.id))
      .map((balanceTag) => {
        const tag_total_expense = Number(balanceTag.tag_total_expense ?? 0);
        return {
          ...balanceTag,
          description: balanceTag.description,
          share: toShare(balanceTag.percentage_of_tagged_expense, tag_total_expense),
        };
      });

    return [...completeTags, ...orphanBalanceTags];
  }, [tagsBalanceData?.tags_balance, tags, taggedExpense]);

  const movementTags = useMemo(
    () =>
      enrichedTags
        .filter((tag) => Number(tag.tag_total_expense ?? 0) > 0)
        .sort((a, b) => Number(b.tag_total_expense ?? 0) - Number(a.tag_total_expense ?? 0)),
    [enrichedTags],
  );

  const sortedTags = useMemo(() => {
    const compare = (first: TagRow, second: TagRow) => {
      if (sortBy === "name-asc" || sortBy === "name-desc") {
        const difference = first.name.localeCompare(second.name, undefined, { sensitivity: "base", numeric: true });
        return sortBy === "name-desc" ? -difference : difference;
      }
      const difference = Number(first.tag_total_expense ?? 0) - Number(second.tag_total_expense ?? 0);
      return sortBy === "expense-asc" ? difference : -difference;
    };
    return [...enrichedTags].sort(compare);
  }, [enrichedTags, sortBy]);

  const filteredTags = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();
    if (!query) return sortedTags;
    return sortedTags.filter((tag) =>
      [tag.name, tag.description].some((value) => value?.toLocaleLowerCase().includes(query)),
    );
  }, [searchTerm, sortedTags]);
  const handleOpenEdit = (tag: SelectedTag) => {
    setSelectedTag(tag);
    setIsModalOpen(true);
  };

  const openTagEditor = (tag: TagRow) => {
    handleOpenEdit({
      id: tag.id,
      name: tag.name,
      description: tag.description,
      color_id: tag.color_id,
      icon_id: tag.icon_id,
    });
  };

  const handleCloseModal = () => {
    setSelectedTag(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (tagData: TagInput, tagId?: number) => {
    if (tagId) await updateTagMutation.mutateAsync({ id: tagId, data: tagData });
    else await createTagMutation.mutateAsync(tagData);
    handleCloseModal();
  };

  const handleDelete = async (tagId: number) => {
    const confirmed = await confirmAction({
      title: t("common.prompts.deleteTag"),
      body: t("common.prompts.cannotUndo", { defaultValue: "This action cannot be undone." }),
      confirmLabel: t("common.actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteTagMutation.mutateAsync(tagId);
    } catch {
      showAppMessage({ title: t("common.errors.deleteTag"), tone: "danger" });
    }
  };

  const handleTagClick = (tag: TagRow) => {
    setDetailsTag(tag);
  };

  if (loadingTagsBalance || loadingTags) {
    return <PageSkeleton lines={5} />;
  }

  if (tagsBalanceError || tagsError) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] px-8 py-6 text-center">
          <p className="font-medium text-[var(--app-text)]">{t("tags.loadError")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page tags-page">
      <div className="app-container max-w-none">
        <DomainTwoColumnLayout className="tags-layout">
          <DomainSidebar className="tags-sidebar">
            <DomainSidebarHeader greeting={t("common:labels.greeting")} title={t("common:nav.tags")} />
            <section className="app-domain-sidebar-section">
              <h2 className="app-domain-sidebar-label">{t("tags.summaryTitle")}</h2>
              <p className="tags-summary-value">{formatMoney(taggedExpense)}</p>
              <p className="tags-summary-note">{t("tags.untaggedShare", { amount: formatMoney(untaggedExpense) })}</p>
            </section>
            <TagDistributionDonut title={t("tags.distributionTitle")} centerLabel={t("tags.summaryTitle")} total={taggedExpense} formattedTotal={formatMoney(taggedExpense)} formattedAmount={formatMoney} formatPercent={formatPercent} otherLabel={t("categories.othersLabel")} mainLabel={t("tags:mainTag", { defaultValue: "Tag principal" })} shareLabel={t("tags:taggedShare", { defaultValue: "de tus gastos taggeados" })} tags={movementTags.map((tag) => ({ id: tag.id, name: tag.name, amount: tag.tag_total_expense, share: tag.share, color: tag.color_id == null ? null : colorById.get(tag.color_id)?.hex, icon: tag.icon_id == null ? null : iconById.get(tag.icon_id)?.name }))} />
          </DomainSidebar>
          <DomainTwoColumnContent className="tags-content">
            {tags.length > 0 && <div role="toolbar" aria-label={t("common.labels.tags")} className="tags-toolbar">
              <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t("tags:searchPlaceholder", { defaultValue: "Buscar tag" })} label={t("tags:searchPlaceholder", { defaultValue: "Buscar tag" })} />
              <SelectInput id="tag-sort" value={sortBy} onChange={(event) => setSortBy(event.target.value as TagSort)} aria-label={t("accounts.sortByLabel")}>
                <option value="expense-desc">{t("accounts.sortByAmount")} ↓</option><option value="expense-asc">{t("accounts.sortByAmount")} ↑</option><option value="name-asc">{t("accounts.sortByName")} A–Z</option><option value="name-desc">{t("accounts.sortByName")} Z–A</option>
              </SelectInput>
            </div>}
            <div className="tags-scroll-content">
            <div className="tags-section-header"><div><h2>{t("tags:yourTags", { defaultValue: "Tus tags" })}</h2><p aria-live="polite">{t("tags:visibleCount", { count: filteredTags.length, defaultValue: "{{count}} tags" })}</p></div></div>
            {filteredTags.length === 0 && <p className="py-8 text-sm text-[var(--ds-ink-muted)]">{searchTerm.trim() ? t("common.messages.noResults", { defaultValue: "No se encontraron resultados." }) : t("tags.emptyStateBody", { defaultValue: "Crea tu primer tag para organizar tus movimientos." })}</p>}
            <div className="tag-card-list">{filteredTags.map((tag) => <TagCard key={tag.id} name={tag.name} color={tag.color_id == null ? null : colorById.get(tag.color_id)?.hex} description={tag.description?.trim() || t("tags.noDescription", { defaultValue: "Sin descripción" })} amount={formatExpenseMoney(tag.tag_total_expense)} percent={tag.share} percentLabel={t("tags:taggedShare", { defaultValue: "de tus gastos taggeados" })} actions={{ edit: t("common:actions.edit"), view: t("categories:viewMovements"), delete: t("common:actions.delete"), menu: t("tags:showActions", { defaultValue: "Acciones del tag" }) }} onOpen={() => handleTagClick(tag)} onEdit={() => openTagEditor(tag)} onDelete={() => handleDelete(tag.id)} />)}</div>
            </div>
          </DomainTwoColumnContent>
        </DomainTwoColumnLayout>
        <NewTagModal isOpen={isModalOpen} onClose={handleCloseModal} onSubmit={handleSubmit} tag={selectedTag} />
        {detailsTag && <EntityTransactionsModal entityType="tag" entityId={detailsTag.id} entityName={detailsTag.name} periodBalance={Number(detailsTag.tag_total_deposit) - Number(detailsTag.tag_total_expense)} onClose={() => setDetailsTag(null)} />}
      </div>
    </div>
  );
};
